import { motion } from 'framer-motion';
import { useI18n } from '../i18n';

export function AboutPage() {
  const { t, language } = useI18n();

  const values = language === 'sr' 
    ? [
        { title: 'Transparentnost', description: 'Svaki oglas prolazi verifikaciju kako bismo osigurali tačnost informacija.' },
        { title: 'Pouzdanost', description: 'Više od 20 godina iskustva u industriji nekretnina.' },
        { title: 'Inovativnost', description: 'AI pomoćnik i napredni filteri za bolju pretragu.' },
        { title: 'Podrška', description: 'Tim stručnjaka uvek spreman da vam pomogne.' },
      ]
    : [
        { title: 'Transparency', description: 'Every listing goes through verification to ensure accuracy.' },
        { title: 'Reliability', description: 'More than 20 years of experience in the real estate industry.' },
        { title: 'Innovation', description: 'AI assistant and advanced filters for better search.' },
        { title: 'Support', description: 'A team of experts always ready to help you.' },
      ];

  const teamHighlights = language === 'sr'
    ? [
        { icon: '🎓', title: 'Stručnost', description: 'Tim sa dugogodišnjim iskustvom u IT-u, nekretninama i digitalnom marketingu.' },
        { icon: '💡', title: 'Iskustvo', description: 'Više od 20 godina kombinovanog iskustva u industriji nekretnina i tehnologije.' },
        { icon: '🤝', title: 'Posvećenost', description: 'Posvećeni smo kreiranju najbolje platforme za nekretnine u Srbiji.' },
        { icon: '🔧', title: 'Podrška', description: 'Dostupni smo za sva vaša pitanja i sugestije putem emaila i telefona.' },
      ]
    : [
        { icon: '🎓', title: 'Expertise', description: 'A team with years of experience in IT, real estate, and digital marketing.' },
        { icon: '💡', title: 'Experience', description: 'Over 20 years of combined experience in real estate and technology.' },
        { icon: '🤝', title: 'Dedication', description: 'We are committed to creating the best real estate platform in Serbia.' },
        { icon: '🔧', title: 'Support', description: 'We are available for all your questions and suggestions via email and phone.' },
      ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            {t.about.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-300 max-w-2xl mx-auto"
          >
            {t.about.subtitle}
          </motion.p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg"
            >
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                {t.about.mission}
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                {t.about.missionText}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg"
            >
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                {t.about.vision}
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                {t.about.visionText}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-12">
            {t.about.values}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-amber-500">{index + 1}</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-4">
            {t.about.team}
          </h2>
          <p className="text-center text-slate-600 dark:text-slate-400 mb-12 max-w-2xl mx-auto">
            {language === 'sr' 
              ? 'Iza platforme Svoj Na Svom stoji tim stručnjaka iz oblasti IT-a, nekretnina i digitalnog marketinga, posvećen stvaranju najboljeg korisničkog iskustva.' 
              : 'Behind the Svoj Na Svom platform stands a team of experts in IT, real estate, and digital marketing, dedicated to creating the best user experience.'}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamHighlights.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t.about.contact}
          </h2>
          <div className="flex flex-wrap justify-center gap-8 text-white/90">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>kancelarija@svojnasvome.com</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>+381 65 290 7002</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Beograd, Srbija</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
